import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Spinner, Badge, ProgressBar, Table, Tabs, Tab } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { Send, Users } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';
import EmailComposer from './EmailComposer';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 }
};

const containerVariants = {
  animate: { transition: { staggerChildren: 0.08 } }
};

const itemVariants = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.35 } }
};

function EmailSender() {
  const [sendMode, setSendMode] = useState('individual');
  const [formData, setFormData] = useState({
    to: '',
    subject: '',
    html: ''
  });
  const [selectedRole, setSelectedRole] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [bulkResult, setBulkResult] = useState(null);
  const [activeTab, setActiveTab] = useState('compose');
  const [sentEmails, setSentEmails] = useState([]);
  const [sentLoading, setSentLoading] = useState(false);
  const [sentSearch, setSentSearch] = useState('');
  const [sentPagination, setSentPagination] = useState({ page: 1, totalPages: 1, total: 0 });

  const roleOptions = [
    { value: 'single', label: 'Singles', icon: '👤', description: 'All single user accounts' },
    { value: 'parent', label: 'Parents', icon: '👪', description: 'All parent accounts' },
    { value: 'shadchan', label: 'Shadchanim', icon: '🤝', description: 'All shadchan accounts' },
    { value: 'all', label: 'All Users', icon: '📧', description: 'Every active account' }
  ];

  const fetchSentEmails = async (page = 1, searchTerm = sentSearch) => {
    setSentLoading(true);
    try {
      const response = await api.get('/admin/email/sent', {
        params: { page, limit: 25, search: searchTerm || undefined }
      });
      setSentEmails(response.data.emails || []);
      setSentPagination(response.data.pagination || { page: 1, totalPages: 1, total: 0 });
    } catch (err) {
      toast.error('Failed to load sent email history');
    } finally {
      setSentLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'sent') {
      fetchSentEmails(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setBulkResult(null);
    setLoading(true);

    try {
      const textContent = formData.html.replace(/<[^>]*>/g, '').trim();
      if (!textContent && attachments.length === 0) {
        setError('Please enter a message or add attachments');
        setLoading(false);
        return;
      }

      if (!formData.subject.trim()) {
        setError('Please enter a subject');
        setLoading(false);
        return;
      }

      if (sendMode === 'byRole') {
        if (!selectedRole) {
          setError('Please select an account type');
          setLoading(false);
          return;
        }

        if (!window.confirm(
          `Are you sure you want to send this email to ALL ${selectedRole === 'all' ? 'users' : selectedRole + ' accounts'}? This cannot be undone.`
        )) {
          setLoading(false);
          return;
        }

        let response;
        if (attachments.length > 0) {
          const formDataToSend = new FormData();
          formDataToSend.append('role', selectedRole);
          formDataToSend.append('subject', formData.subject);
          formDataToSend.append('html', formData.html);
          attachments.forEach((att) => {
            formDataToSend.append('attachments', att.file);
          });
          response = await api.post('/admin/email/send-by-role', formDataToSend, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } else {
          response = await api.post('/admin/email/send-by-role', {
            role: selectedRole,
            subject: formData.subject,
            html: formData.html
          });
        }

        setBulkResult(response.data);
        if (response.data.failed === 0) {
          toast.success(`Email sent to ${response.data.sent} ${selectedRole === 'all' ? 'users' : selectedRole + ' accounts'}!`);
        } else {
          toast.warning(`Sent to ${response.data.sent}, failed for ${response.data.failed} recipients`);
        }

        setFormData({ to: '', subject: '', html: '' });
        setSelectedRole('');
        setAttachments([]);
      } else {
        const recipients = formData.to.split(',').map(email => email.trim()).filter(email => email);

        if (recipients.length === 0) {
          setError('Please enter at least one recipient email address');
          setLoading(false);
          return;
        }

        if (attachments.length > 0) {
          const formDataToSend = new FormData();
          formDataToSend.append('to', recipients.length === 1 ? recipients[0] : JSON.stringify(recipients));
          formDataToSend.append('subject', formData.subject);
          formDataToSend.append('html', formData.html);
          attachments.forEach((att) => {
            formDataToSend.append('attachments', att.file);
          });
          await api.post('/admin/email/send', formDataToSend, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } else {
          await api.post('/admin/email/send', {
            to: recipients.length === 1 ? recipients[0] : recipients,
            subject: formData.subject,
            html: formData.html
          });
        }

        toast.success('Email sent successfully!');
        setFormData({ to: '', subject: '', html: '' });
        setAttachments([]);
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to send email';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <div className="page-header">
        <h1 className="page-title">Send Email</h1>
        <p className="page-subtitle">Compose and send branded emails to users</p>
      </div>

      <div className="glass-card" style={{ marginBottom: '24px', padding: '14px 18px' }}>
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k || 'compose')}
          className="mb-0"
        >
          <Tab eventKey="compose" title="Compose" />
          <Tab eventKey="sent" title="Sent Emails" />
        </Tabs>
      </div>

      {activeTab === 'compose' && (
      <motion.div
        variants={containerVariants}
        initial="initial"
        animate="animate"
        style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
      >
        {/* Main Compose Card */}
        <motion.div className="glass-card" variants={itemVariants}>
          <div className="card-header-row">
            <h3 className="card-title">Compose Email</h3>
            <span className="card-subtitle">Resunize branding applied automatically</span>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {bulkResult && (
            <Alert
              variant={bulkResult.failed === 0 ? 'success' : 'warning'}
              dismissible
              onClose={() => setBulkResult(null)}
              style={{ marginBottom: '20px' }}
            >
              <strong>Bulk Email Results</strong>
              <div style={{ marginTop: '8px', display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '13px' }}>
                <span>Role: <strong>{bulkResult.role === 'all' ? 'All Users' : bulkResult.role}</strong></span>
                <span>Recipients: <strong>{bulkResult.totalRecipients}</strong></span>
                <span>
                  <Badge bg="success" style={{ marginRight: '4px' }}>Sent: {bulkResult.sent}</Badge>
                  {bulkResult.failed > 0 && <Badge bg="danger">Failed: {bulkResult.failed}</Badge>}
                </span>
              </div>
              {bulkResult.sent > 0 && (
                <ProgressBar
                  style={{ marginTop: '12px' }}
                  now={(bulkResult.sent / bulkResult.totalRecipients) * 100}
                  variant={bulkResult.failed === 0 ? 'success' : 'warning'}
                  label={`${Math.round((bulkResult.sent / bulkResult.totalRecipients) * 100)}%`}
                />
              )}
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            {/* Send Mode Toggle */}
            <Form.Group style={{ marginBottom: '20px' }}>
              <Form.Label className="fw-bold">Send To</Form.Label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  variant={sendMode === 'individual' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => { setSendMode('individual'); setSelectedRole(''); }}
                >
                  <Send size={14} /> Individual Emails
                </Button>
                <Button
                  variant={sendMode === 'byRole' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => { setSendMode('byRole'); setFormData(prev => ({ ...prev, to: '' })); }}
                >
                  <Users size={14} /> By Account Type
                </Button>
              </div>
            </Form.Group>

            {sendMode === 'individual' ? (
              <Form.Group style={{ marginBottom: '20px' }}>
                <Form.Label>To (Email Addresses)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="user@example.com or user1@example.com, user2@example.com"
                  value={formData.to}
                  onChange={(e) => setFormData({ ...formData, to: e.target.value })}
                  required
                />
                <Form.Text className="text-muted">
                  Enter one email address, or multiple separated by commas
                </Form.Text>
              </Form.Group>
            ) : (
              <Form.Group style={{ marginBottom: '20px' }}>
                <Form.Label>Account Type</Form.Label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {roleOptions.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setSelectedRole(option.value)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '12px 18px',
                        minWidth: '160px',
                        border: selectedRole === option.value
                          ? '2px solid var(--accent)'
                          : '1px solid var(--border-medium)',
                        borderRadius: 'var(--radius-md)',
                        background: selectedRole === option.value
                          ? 'var(--accent-light)'
                          : 'var(--surface-solid)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        textAlign: 'left'
                      }}
                    >
                      <span style={{ fontSize: '1.3em' }}>{option.icon}</span>
                      <div>
                        <div style={{
                          fontWeight: 600,
                          fontSize: '13px',
                          color: selectedRole === option.value ? 'var(--accent)' : 'var(--text-primary)',
                          lineHeight: 1.2
                        }}>
                          {option.label}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                          {option.description}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                {selectedRole && (
                  <Form.Text className="text-muted" style={{ marginTop: '8px', display: 'block' }}>
                    This will send to <strong>all active {selectedRole === 'all' ? '' : selectedRole} accounts</strong>
                  </Form.Text>
                )}
              </Form.Group>
            )}

            <Form.Group style={{ marginBottom: '20px' }}>
              <Form.Label>Subject</Form.Label>
              <Form.Control
                type="text"
                placeholder="Email subject"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group style={{ marginBottom: '24px' }}>
              <Form.Label>Message</Form.Label>
              <EmailComposer
                message={formData.html}
                setMessage={(html) => setFormData({ ...formData, html })}
                attachments={attachments}
                setAttachments={setAttachments}
                placeholder="Type your message here... Use the formatting buttons for bold, italic, and underline."
              />
              <Form.Text className="text-muted">
                The email will be automatically wrapped in Resunize notebook-themed branding.
              </Form.Text>
            </Form.Group>

            <Button
              variant={sendMode === 'byRole' ? 'warning' : 'primary'}
              type="submit"
              disabled={loading}
              style={{ padding: '10px 24px' }}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" />
                  {sendMode === 'byRole' ? 'Sending to all...' : 'Sending...'}
                </>
              ) : (
                <>
                  {sendMode === 'byRole' ? <Users size={16} /> : <Send size={16} />}
                  {sendMode === 'byRole'
                    ? `Send to All ${selectedRole === 'all' ? 'Users' : selectedRole ? roleOptions.find(r => r.value === selectedRole)?.label : '...'}`
                    : 'Send Email'
                  }
                </>
              )}
            </Button>
          </Form>
        </motion.div>

        {/* Template Examples */}
        <motion.div className="glass-card" variants={itemVariants}>
          <div className="card-header-row">
            <h3 className="card-title">Template Examples</h3>
            <span className="card-subtitle">Copy and customize</span>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <h4 style={{
              fontSize: '13px',
              fontWeight: 600,
              color: 'var(--text-secondary)',
              marginBottom: '8px'
            }}>
              Simple Text Message
            </h4>
            <pre style={{
              background: 'var(--bg-subtle)',
              padding: '16px',
              borderRadius: 'var(--radius-sm)',
              fontSize: '12.5px',
              color: 'var(--text-secondary)',
              border: '1px solid var(--border-light)',
              margin: 0,
              whiteSpace: 'pre-wrap'
            }}>
{`<p>Hello,</p>
<p>This is a simple message to inform you about...</p>
<p>Best regards,<br/>Resunize Team</p>`}
            </pre>
          </div>

          <div>
            <h4 style={{
              fontSize: '13px',
              fontWeight: 600,
              color: 'var(--text-secondary)',
              marginBottom: '8px'
            }}>
              Message with Link
            </h4>
            <pre style={{
              background: 'var(--bg-subtle)',
              padding: '16px',
              borderRadius: 'var(--radius-sm)',
              fontSize: '12.5px',
              color: 'var(--text-secondary)',
              border: '1px solid var(--border-light)',
              margin: 0,
              whiteSpace: 'pre-wrap'
            }}>
{`<p>Hello,</p>
<p>Please click the link below to access your account:</p>
<p><a href="https://resunize.com">Visit Resunize</a></p>
<p>Thank you!</p>`}
            </pre>
          </div>
        </motion.div>
      </motion.div>
      )}

      {activeTab === 'sent' && (
        <motion.div variants={containerVariants} initial="initial" animate="animate">
          <motion.div className="glass-card" variants={itemVariants}>
            <div className="card-header-row">
              <h3 className="card-title">Sent Emails</h3>
              <span className="card-subtitle">{sentPagination.total || 0} total</span>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
              <Form.Control
                type="text"
                placeholder="Search by subject or sender..."
                value={sentSearch}
                onChange={(e) => setSentSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') fetchSentEmails(1, sentSearch);
                }}
              />
              <Button variant="outline-primary" onClick={() => fetchSentEmails(1, sentSearch)}>
                Search
              </Button>
            </div>

            {sentLoading ? (
              <div className="page-loading" style={{ minHeight: '180px' }}>
                <div className="loading-spinner" />
              </div>
            ) : sentEmails.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>No sent email records found.</div>
            ) : (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <Table hover responsive>
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Subject</th>
                        <th>Mode</th>
                        <th>Recipients</th>
                        <th>Result</th>
                        <th>By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sentEmails.map((row) => (
                        <tr key={row.id}>
                          <td style={{ whiteSpace: 'nowrap' }}>{new Date(row.created_at).toLocaleString()}</td>
                          <td>{row.subject}</td>
                          <td>
                            <Badge bg={row.send_mode === 'byRole' ? 'warning' : 'primary'}>
                              {row.send_mode === 'byRole' ? 'By Role' : 'Individual'}
                            </Badge>
                          </td>
                          <td>{row.total_recipients || 0}</td>
                          <td>
                            <Badge bg="success" style={{ marginRight: '6px' }}>Sent {row.sent_count || 0}</Badge>
                            {(row.failed_count || 0) > 0 && <Badge bg="danger">Failed {row.failed_count}</Badge>}
                          </td>
                          <td>{row.sent_by_email || 'System'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                {sentPagination.totalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                      Page {sentPagination.page} of {sentPagination.totalPages}
                    </span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        disabled={sentPagination.page <= 1}
                        onClick={() => fetchSentEmails(sentPagination.page - 1, sentSearch)}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        disabled={sentPagination.page >= sentPagination.totalPages}
                        onClick={() => fetchSentEmails(sentPagination.page + 1, sentSearch)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
}

export default EmailSender;
